import React from "react";
import Immutable from "immutable";

import useDimensions from "js/common/utils/use-dimensions";
import * as SavedConfigs from "js/common/saved-configs";
import * as Popups from "js/common/popups";
import * as Auditor from "js/common/auditor";
import * as Rata from "js/common/utils/remote-data";
import * as Users from "js/common/users";
import * as DashboardUtils from "js/dashboards/utils";
import * as Branding from "js/common/branding-constants";
import TextField from "js/common/views/inputs/text-field";

import {CustomThemeContext} from "js/common/themes/CustomThemeProvider";
import {TextButton} from "js/common/views/inputs/buttons";
import {FormControlLabel, Switch} from "@mui/material";
import SaveReportAsDialog from "js/common/views/save-as-dialog";
import SaveButton from "js/dashboards/toolbar/save-button";
import UnsavedChangesIndicator from "js/dashboards/toolbar/unsaved-changes-indicator";
import AiAssistantButton from "js/dashboards/toolbar/ai-assistant-button";
import currentClient from "js/common/repo/backbone/current-client";
import {clientPermissions} from "js/common/permissions";
import * as SharingUtils from "js/common/views/sharing/utils";

const DashboardToolbar = React.memo(({
      dashboard,
      onDashboardChange,
      setSavingDashboard,
      hasUnsavedChanges,
      handleDashboardSave,
      handleDashboardSaveAs,

      isEditing,
      setEditing,

      ownershipTypeToDashboardsWrapper,
      setOwnershipTypeToDashboardsWrapper,

      onRequestOpenSavedDashboards,
      onRequestOpenComponentMenu,
      onRequestOpenAiAssistant,

      onRequestReloadDashboard,
      onRequestNewDashboard,
      handleNameChange
    }) => {
      const {isFullEditor, isBasicEditor} = DashboardUtils.useEditorFlags();

      const [showSaveAsDialog, setShowSaveAsDialog] = React.useState(false);

      const openAiAssistant = () => {
        Auditor.audit("ai-assistant-clicked", {from: "dashboards-main"});
        onRequestOpenAiAssistant();
      };

      const handleSaveAsRequest = React.useCallback(
          dashboardName => {
            setShowSaveAsDialog(false);
            setSavingDashboard(true);
            SavedConfigs
                .create(dashboard.set("name", dashboardName).delete("systemDefault"))
                .then(
                    savedDashboard => {
                      Popups.namedItemSaved(dashboardName);
                      Auditor.audit("dashboards:save-as", {dashboardName});
                      handleDashboardSaveAs(savedDashboard);
                      onDashboardChange(savedDashboard);
                      setOwnershipTypeToDashboardsWrapper(wrapper => Rata
                          .updateValue(wrapper, x => x
                              .update("ownedConfigs", ownedConfigs => ownedConfigs.push(savedDashboard))));
                    },
                    err => {
                      const errorMsg = "Unable to save dashboard";
                      if (err.responseJSON?.message) {
                        Popups.error(`${errorMsg}: ${err.responseJSON.message}`);
                      } else {
                        Popups.error(errorMsg);
                      }
                    })
                .finally(() => setSavingDashboard(false));
          },
          [
            dashboard,
            handleDashboardSaveAs,
            onDashboardChange,
            setOwnershipTypeToDashboardsWrapper,
            setSavingDashboard,
            setShowSaveAsDialog]);

      const handleSaveRequest = React.useCallback(() => {
        const id = dashboard.get("id");
        const name = dashboard.get("name");
        setSavingDashboard(true);
        SavedConfigs
            .update(dashboard)
            .then(
                savedDashboard => {
                  Popups.namedItemUpdated(name);
                  Auditor.audit("dashboards:save", {id, name});
                  handleDashboardSave(savedDashboard);
                  onDashboardChange(savedDashboard);
                  setOwnershipTypeToDashboardsWrapper(wrapper => {
                    const index = Rata.getValue(wrapper)
                        .get("ownedConfigs")
                        .findIndex(ownedConfig => ownedConfig.get("id") === id);
                    return Rata.updateValue(wrapper, x => x
                        .update("ownedConfigs", ownedConfigs => ownedConfigs.set(index, savedDashboard)));
                  });
                },
                err => {
                  const errorMsg = "Unable to save dashboard";
                  if (err.responseJSON?.message) {
                    Popups.error(`${errorMsg}: ${err.responseJSON.message}`);
                  } else {
                    Popups.error(errorMsg);
                  }
                })
            .finally(() => setSavingDashboard(false));
      }, [dashboard, onDashboardChange, handleDashboardSave, setOwnershipTypeToDashboardsWrapper, setSavingDashboard]);

      const {theme} = React.useContext(CustomThemeContext);

      const [dimRef, dimensions] = useDimensions();
      const {width} = dimensions;
      const smallResolution = width <= 1024;

      const userHasPermission = React.useMemo(() => {
        if (!isFullEditor) {
          return false;
        }
        if (dashboard?.has("sharedWith")) {
          const permission = SharingUtils.getCurrentUserPermissionForConfig(dashboard);
          return permission === "EDIT";
        }
        return false;
      }, [dashboard, isFullEditor]);

      const hasAiAssistant = currentClient.hasPermission(clientPermissions.canUseAiAssistant) && isFullEditor;
      const dashboardOwnerId = dashboard?.get("ownerId");
      const isDashboardOwner = dashboardOwnerId === Users.getCurrentUser().get("id");
      const isDashboardOwnerOrEditor = isDashboardOwner || userHasPermission;
      const handleSaveClick = dashboard?.get("id") ? handleSaveRequest : () => setShowSaveAsDialog(true);
      const isSystemDefault = dashboard?.get("systemDefault");
      const isSaveRestricted = isSystemDefault || !isDashboardOwnerOrEditor;
      const saveTooltipText = isSaveRestricted
          ? "This dashboard is owned by another user or is generated by Bullhorn and cannot be edited. Use Save As instead."
          : "";

      return <div
          ref={dimRef}
          style={{
            backgroundColor: theme.palette.background.card,
            boxShadow: "rgb(0 0 0 / 20%) 0px 2px 1px -1px",
            display: "flex",
            padding: "0.8rem 1.5rem",
            marginBottom: "1rem"
          }}>
        <div style={{flex: 1, display: "flex", alignItems: "center"}}>
          {isFullEditor && <TextButton
              className="hide-for-print"
              testId="new-dashboard-button"
              style={{marginRight: "0.5rem", fontSize: "12px", paddingRight: 15, paddingLeft: 15}}
              type="inverted"
              label={`${smallResolution ? "" : "New"}`}
              iconTyp="bhi"
              icon="plus"
              onClick={onRequestNewDashboard} />}
          <div style={{position: "relative"}}>
            <SaveButton
                className="hide-for-print"
                disabled={isSaveRestricted}
                showUnsavedChangesIndicator={!isSaveRestricted && hasUnsavedChanges}
                label={smallResolution ? "" : "Save"}
                onClick={handleSaveClick}
                tooltipText={saveTooltipText} />
          </div>
          <div style={{position: "relative"}}>
            <TextButton
                className="hide-for-print"
                testId="save-as-dashboard-button"
                style={{marginRight: "0.5rem", fontSize: "12px", whiteSpace: "nowrap", paddingRight: 15, paddingLeft: 15}}
                label={`${smallResolution ? "" : "Save As"}`}
                iconType="bhi"
                icon="save"
                stackedIcon="edit"
                type="inverted"
                onClick={() => setShowSaveAsDialog(true)} />
            {(hasUnsavedChanges && isSaveRestricted) && <UnsavedChangesIndicator />}
          </div>
          <TextButton
              className="hide-for-print"
              dataProductId="open-dashboard-button"
              testId="open-dashboard-button"
              style={{marginRight: "0.5rem", fontSize: "12px", paddingRight: 15, paddingLeft: 15}}
              type="inverted"
              iconType="bhi"
              icon="file"
              label={`${smallResolution ? "" : "Open"}`}
              onClick={onRequestOpenSavedDashboards} />
        </div>
        <div style={{display: "flex", alignItems: "center"}}>
          {dashboard && <h3 style={{textAlign: "center", fontSize: "1em"}}>{isEditing ? <TextField
                  value={dashboard.get("name")}
                  data-test-id="edit-dashboard-name"
                  onChange={e => handleNameChange(e.target.value)}
                  style={{width: 300, paddingTop: 3, paddingLeft: 10}} />
              : dashboard.get("name")}</h3>}
        </div>
        <div style={{flex: 1, display: "flex", justifyContent: "flex-end", alignItems: "center"}}>
          {hasAiAssistant && <AiAssistantButton
              className="hide-for-print"
              label={smallResolution ? "" : Branding.aiAssistantName}
              onClick={openAiAssistant} />}
          {(isFullEditor && isEditing) && <TextButton
              className="hide-for-print"
              dataProductId="add-panel-button"
              testId="add-panel-button"
              style={{
                marginLeft: hasAiAssistant? 15 : 0,
                marginRight: "1.5rem",
                fontSize: "12px",
                whiteSpace: "nowrap",
                paddingRight: 15,
                paddingLeft: 15
              }}
              disabled={dashboard.getIn(["json", "layoutType"]) === "window-only"
                  && dashboard.getIn(["json", "components"], Immutable.List()).size >= 1}
              type="inverted"
              label={`${smallResolution ? "" : "Add Panel"}`}
              iconType="bhi"
              icon="addcard"
              onClick={onRequestOpenComponentMenu} />}
          {isBasicEditor && <FormControlLabel
              className="hide-for-print"
              componentsProps={{typography: {fontSize: 14}}}
              label="Edit"
              style={{
                marginLeft: hasAiAssistant && !isEditing? 15 : -11,
                whiteSpace: "nowrap",
                marginRight: "1.5em"}}
              control={<Switch
                  data-product-id="edit-mode-switch"
                  data-test-id="toggle-edit-mode"
                  checked={isEditing}
                  onChange={e => {
                    e.target.checked
                        ? document.body.classList.add("isEditing")
                        : document.body.classList.remove("isEditing");
                    setEditing(e.target.checked);
                  }} />} />}
          <TextButton
              className="hide-for-print"
              icon="refresh" iconType="bhi" label="" onClick={onRequestReloadDashboard}
              style={{fontSize: "12px", padding: 0, minWidth: 50}} />
        </div>

        {showSaveAsDialog && <SaveReportAsDialog
            initialName={dashboard.get("name")}
            savingTypeName="dashboard"
            generateUniqueName={false}
            onSaveAsRequest={handleSaveAsRequest}
            onCancelRequest={() => setShowSaveAsDialog(false)}
            unavailableNames={Rata
                .getValue(ownershipTypeToDashboardsWrapper)
                .get("ownedConfigs")
                .map(config => config.get("name"))} />}
      </div>;
    })
;

export default DashboardToolbar;
